// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { hiraganas } from "../consts/hiraganaList";
import { katakana } from "../consts/katakana";

const romajiBySymbol = new Map();
for (const { symbol, romaji } of [...hiraganas, ...katakana]) {
    if (!romaji || romaji.startsWith("small-") || romaji === "dot" || romaji === "space") continue;
    if (!romajiBySymbol.has(symbol)) romajiBySymbol.set(symbol, romaji);
}

const smallTsuChars = new Set(["っ", "ッ"]);
const longVowelChars = new Set(["ー"]);
const consonantPattern = /^[bcdfghjklmnpqrstvwxyz]$/;

// Hepburn separates ん from a vowel or y-sound that follows it with an
// apostrophe, so the n is not read as the start of the next mora: 翻訳 is
// "hon'yaku" (not ほにゃく), 原因 "gen'in" (not げにん). Anything else after ん
// (a consonant, ん + にゃ = "nnya", a long-vowel mark, the end of the text)
// keeps the plain "n". The check looks at the very next character only: the
// "." and "-" notation marks are not skipped.
const moraicNChars = new Set(["ん", "ン"]);
const apostropheTriggerChars = new Set([..."あいうえおやゆよアイウエオヤユヨ"]);

// Finds the romaji for the next actual kana mora, skipping over "." and "-"
// notation characters (KANJIDIC's okurigana/suffix markers aren't part of the
// pronunciation, so a small tsu right before one still doubles the consonant
// that follows it, e.g. "やっ.つ" -> "yat.tsu").
function peekRomaji(chars, index) {
    let i = index;
    while (i < chars.length && (chars[i] === "." || chars[i] === "-")) i += 1;
    if (i >= chars.length) return null;
    const two = chars.slice(i, i + 2).join("");
    if (romajiBySymbol.has(two)) return romajiBySymbol.get(two);
    return romajiBySymbol.get(chars[i]) || null;
}

// Converts a kana reading (as found in KANJIDIC, e.g. "い.く" or "-ゆき") to
// romaji, keeping the "." okurigana boundary and leading "-" suffix marker
// from the source notation as literal separators.
export function kanaToRomaji(text) {
    const chars = [...String(text || "")];
    let result = "";
    let i = 0;

    while (i < chars.length) {
        const char = chars[i];

        if (char === "." || char === "-") {
            result += char;
            i += 1;
            continue;
        }

        if (smallTsuChars.has(char)) {
            const next = peekRomaji(chars, i + 1);
            if (next) {
                result += next.startsWith("ch") ? "t" : consonantPattern.test(next[0]) ? next[0] : "";
            }
            i += 1;
            continue;
        }

        if (longVowelChars.has(char)) {
            result += result.slice(-1);
            i += 1;
            continue;
        }

        const two = chars.slice(i, i + 2).join("");
        if (romajiBySymbol.has(two)) {
            result += romajiBySymbol.get(two);
            i += 2;
            continue;
        }

        if (romajiBySymbol.has(char)) {
            result += romajiBySymbol.get(char);
            if (moraicNChars.has(char) && apostropheTriggerChars.has(chars[i + 1])) result += "'";
            i += 1;
            continue;
        }

        result += char;
        i += 1;
    }

    return result;
}
